import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { X } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { createClient, deleteClient, getClientJobs, listClients, updateClient, type Client } from "../api/clients.ts";

export default function ClientsPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState<Client | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["clients", q],
    queryFn: () => listClients({ q: q || undefined, limit: 100 }),
  });

  const { data: clientJobs } = useQuery({
    queryKey: ["client-jobs", selected?.id],
    queryFn: () => getClientJobs(selected!.id),
    enabled: !!selected,
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteClient(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["clients"] });
      setSelected(null);
    },
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-slate-900">{t("clients.title", "Clients")}</h1>
        <div className="flex items-center gap-2">
          <input
            className="input w-full sm:max-w-xs"
            placeholder={t("common.search") ?? "Search…"}
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <button type="button" className="btn-primary" onClick={() => setShowCreate(true)}>
            + {t("clients.new", "New client")}
          </button>
        </div>
      </div>

      <div className="table-wrapper">
        <table className="table-base">
          <thead>
            <tr>
              <th>Name</th>
              <th>Phone</th>
              <th className="hidden md:table-cell">Email</th>
              <th className="hidden md:table-cell">LTV</th>
              <th className="hidden md:table-cell">Last service</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500">Loading…</td></tr>
            )}
            {!isLoading && data?.items.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500">No clients found.</td></tr>
            )}
            {data?.items.map((c) => (
              <tr
                key={c.id}
                className="cursor-pointer hover:bg-slate-50"
                onClick={() => { setSelected(c); setShowCreate(false); }}
              >
                <td className="px-4 py-2 font-medium">{c.name ?? "(no name)"}</td>
                <td className="px-4 py-2">{c.phone_e164}</td>
                <td className="hidden px-4 py-2 text-slate-500 md:table-cell">{c.email ?? "—"}</td>
                <td className="hidden px-4 py-2 md:table-cell">{c.lifetime_value_cents > 0 ? `$${(c.lifetime_value_cents / 100).toFixed(2)}` : "—"}</td>
                <td className="hidden px-4 py-2 text-slate-500 md:table-cell">
                  {c.last_service_at ? new Date(c.last_service_at).toLocaleDateString() : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selected && !showCreate && (
        <ClientDrawer
          client={selected}
          jobs={clientJobs ?? []}
          onClose={() => setSelected(null)}
          onDeleted={() => deleteMut.mutate(selected.id)}
          onUpdated={(updated) => {
            setSelected(updated);
            qc.invalidateQueries({ queryKey: ["clients"] });
          }}
        />
      )}

      {showCreate && (
        <CreateClientModal
          onClose={() => setShowCreate(false)}
          onCreated={(c) => {
            qc.invalidateQueries({ queryKey: ["clients"] });
            setShowCreate(false);
            setSelected(c);
          }}
        />
      )}
    </div>
  );
}

function ClientDrawer({
  client,
  jobs,
  onClose,
  onDeleted,
  onUpdated,
}: {
  client: Client;
  jobs: { id: string; job_number: number | null; title: string | null; status: string; scheduled_start: string | null; created_at: string }[];
  onClose: () => void;
  onDeleted: () => void;
  onUpdated: (c: Client) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(client.name ?? "");
  const [email, setEmail] = useState(client.email ?? "");
  const [notes, setNotes] = useState(client.notes ?? "");

  const saveMut = useMutation({
    mutationFn: () => updateClient(client.id, { name: name || null, email: email || null, notes: notes || null }),
    onSuccess: (updated) => { onUpdated(updated); setEditing(false); },
  });

  return (
    <div className="fixed inset-y-0 end-0 z-50 flex w-full max-w-md flex-col border-s border-slate-200 bg-white shadow-xl">
      <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4 bg-slate-50">
        <h2 className="text-lg font-bold text-slate-900">{client.name ?? client.phone_e164}</h2>
        <button type="button" onClick={onClose} className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600">
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
        {editing ? (
          <div className="space-y-4">
            <div className="field">
              <label className="field-label">Name</label>
              <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="field">
              <label className="field-label">Email</label>
              <input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            <div className="field">
              <label className="field-label">Notes</label>
              <textarea className="input" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
            </div>
            <div className="flex gap-2">
              <button type="button" className="btn-primary" onClick={() => saveMut.mutate()} disabled={saveMut.isPending}>
                {saveMut.isPending ? "Saving…" : "Save"}
              </button>
              <button type="button" className="btn-secondary" onClick={() => setEditing(false)}>Cancel</button>
            </div>
          </div>
        ) : (
          <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
            <dt className="text-slate-500">Phone</dt>
            <dd className="font-mono">{client.phone_e164}</dd>
            <dt className="text-slate-500">Email</dt>
            <dd>{client.email ?? "—"}</dd>
            <dt className="text-slate-500">Locale</dt>
            <dd>{client.preferred_locale}</dd>
            <dt className="text-slate-500">Lifetime value</dt>
            <dd>{client.lifetime_value_cents > 0 ? `$${(client.lifetime_value_cents / 100).toFixed(2)}` : "—"}</dd>
            <dt className="text-slate-500">Last service</dt>
            <dd>{client.last_service_at ? new Date(client.last_service_at).toLocaleDateString() : "—"}</dd>
            {client.notes && (
              <>
                <dt className="text-slate-500">Notes</dt>
                <dd className="col-span-2 whitespace-pre-wrap text-xs text-slate-700">{client.notes}</dd>
              </>
            )}
          </dl>
        )}

        <div>
          <h3 className="mb-2 text-sm font-semibold text-slate-700">Job history</h3>
          {jobs.length === 0 ? (
            <p className="text-xs text-slate-400">No jobs yet.</p>
          ) : (
            <ul className="space-y-1 text-sm">
              {jobs.map((j) => (
                <li key={j.id} className="flex items-center justify-between rounded border border-slate-100 px-3 py-2">
                  <Link to={`/jobs/${j.id}`} className="font-medium text-brand-700 hover:underline">
                    {j.job_number ? `#${j.job_number}` : ""} {j.title ?? "(untitled)"}
                  </Link>
                  <span className="rounded bg-slate-100 px-2 py-0.5 text-xs">{j.status}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="border-t border-slate-200 px-5 py-3 flex justify-between">
        {!editing && (
          <button type="button" className="btn-secondary" onClick={() => setEditing(true)}>Edit</button>
        )}
        <button
          type="button"
          className="ms-auto text-sm text-red-600 hover:underline"
          onClick={() => { if (window.confirm(`Delete ${client.name ?? client.phone_e164}?`)) onDeleted(); }}
        >
          Delete
        </button>
      </div>
    </div>
  );
}

function CreateClientModal({ onClose, onCreated }: { onClose: () => void; onCreated: (c: Client) => void }) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState("");

  const mut = useMutation({
    mutationFn: () => createClient({ name: name || null, phone_e164: phone, email: email || null, notes: notes || null }),
    onSuccess: onCreated,
    onError: (e: Error) => setError(e.message),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-xl">
        <div className="mb-5 flex items-center justify-between">
          <h2 className="text-lg font-bold text-slate-900">New client</h2>
          <button type="button" onClick={onClose} className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600"><X className="h-4 w-4" /></button>
        </div>
        <div className="space-y-3">
          <div className="field">
            <label className="field-label">Name</label>
            <input className="input" placeholder="John Smith" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="field">
            <label className="field-label">Phone (E.164) *</label>
            <input className="input" placeholder="+15125551234" value={phone} onChange={(e) => setPhone(e.target.value)} />
          </div>
          <div className="field">
            <label className="field-label">Email</label>
            <input className="input" type="email" placeholder="customer@email.com" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div className="field">
            <label className="field-label">Notes</label>
            <textarea className="input" rows={2} placeholder="Any relevant notes…" value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="flex gap-2 pt-1">
            <button type="button" className="btn-primary w-full" onClick={() => mut.mutate()} disabled={!phone || mut.isPending}>
              {mut.isPending ? "Creating…" : "Create"}
            </button>
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}
